import { __decorate, __metadata } from "tslib";
import { Component, ModelSync, Prop, Ref, Watch } from "vue-property-decorator";
import { DateTime, Info, Duration } from "luxon";
import LvField from "../field/LvField.vue";
import LvControlPopover from "../control-popover/LvControlPopover.vue";
import { UIComponent } from "../UIComponent";
import { getLocaleDateFormat, parseLocaleDate } from "@fastoche/ui-core/i18n/date";
import { LvIcon } from "../icon";
import { LvCalendarMonth } from "../calendar-month";
const DEFAULT_START_YEAR = new Date().getFullYear() - 120;
const DEFAULT_END_YEAR = new Date().getFullYear() + 50;
const YEARS = Array(DEFAULT_END_YEAR - DEFAULT_START_YEAR + 1).fill(0).map((_, idx) => DEFAULT_START_YEAR + idx);
export const COMPONENT_NAME = "LvDateRangePicker";
/**
 * A component to select a date range
 */
let LvDateRangePicker = class LvDateRangePicker extends UIComponent {
    constructor() {
        super(...arguments);
        this.calendarMonthDate = DateTime.now().startOf("month");
        this.localeFormat = getLocaleDateFormat();
        this.inputs = {
            from: "",
            to: "",
        };
    }
    mounted() {
        this.initialize();
    }
    initialize() {
        this.localeFormat = getLocaleDateFormat();
        this.onModelChange();
    }
    /**
     * Show the date range picker popup
     */
    show() {
        this.popover.show();
    }
    /**
     * Hide the date range picker popup
     */
    hide() {
        this.popover.hide();
    }
    /**
     * Display the previous month.
     * <br>This does not change the selected date range.
     */
    prevMonth() {
        this.calendarMonthDate = this.calendarMonthDate.minus({ month: 1 });
    }
    /**
     * Display the next month.
     * <br>This does not change the selected date range.
     */
    nextMonth() {
        this.calendarMonthDate = this.calendarMonthDate.plus({ month: 1 });
    }
    get nextCalendarMonthDate() {
        return this.calendarMonthDate.plus({ month: 1 });
    }
    get years() {
        let years = YEARS;
        if (this.minDate) {
            const minYear = DateTime.fromISO(this.minDate).year;
            years = years.filter(x => x >= minYear);
        }
        if (this.maxDate) {
            const maxYear = DateTime.fromISO(this.maxDate).year;
            years = years.filter(x => x <= maxYear);
        }
        return years;
    }
    get months() {
        return Info.months("long");
    }
    get month() {
        return this.calendarMonthDate.monthLong;
    }
    set month(value) {
        const monthNumber = this.months.indexOf(value) + 1;
        this.calendarMonthDate = this.calendarMonthDate.set({ month: monthNumber });
    }
    get year() {
        return this.calendarMonthDate.year;
    }
    set year(value) {
        this.calendarMonthDate = this.calendarMonthDate.set({ year: value });
    }
    onModelChange() {
        if (!this.model) {
            this.inputs = {
                from: "",
                to: "",
            };
            if (this.minDate) {
                this.calendarMonthDate = DateTime.fromISO(this.minDate).startOf("month");
            }
            else if (this.maxDate) {
                this.calendarMonthDate = DateTime.fromISO(this.maxDate).startOf("month");
            }
        }
        else {
            const [nativeFrom, nativeTo] = this.model;
            const from = DateTime.fromISO(nativeFrom);
            this.calendarMonthDate = from.startOf("month");
            this.inputs.from = from.toFormat(this.localeFormat.displayFormat);
            if (nativeTo) {
                const to = DateTime.fromISO(nativeTo);
                this.inputs.to = to.toFormat(this.localeFormat.displayFormat);
            }
            else {
                this.inputs.to = "";
            }
        }
    }
    onInputChange() {
        if (!this.inputs.from) {
            if (this.model?.[0]) {
                this.model = null;
            }
        }
        else {
            const date = parseLocaleDate(this.inputs.from);
            if (date && date.isValid && !this.isDayDisabled(date.toISO())) {
                if (this.model) {
                    this.$set(this.model, 0, date.toISO());
                }
                else {
                    this.model = [date.toISO(), null];
                }
            }
            else {
                this.model = null;
                this.inputs.from = "";
            }
        }
        if (!this.inputs.to) {
            if (this.model) {
                this.$set(this.model, 1, null);
            }
        }
        else {
            const date = parseLocaleDate(this.inputs.to);
            if (date && date.isValid && !this.isDayDisabled(date.toISO())) {
                if (this.model) {
                    this.$set(this.model, 1, date.toISO());
                }
            }
            else {
                if (this.model) {
                    this.$set(this.model, 1, null);
                }
                this.inputs.to = "";
            }
        }
    }
    isDayDisabled(day) {
        const predicates = [];
        const dayDateTime = DateTime.fromISO(day).startOf("day");
        if (this.minDate) {
            const minDateTime = DateTime.fromISO(this.minDate).startOf("day");
            predicates.push(d => d < minDateTime);
        }
        if (this.maxDate) {
            const maxDateTime = DateTime.fromISO(this.maxDate).startOf("day");
            predicates.push(d => d > maxDateTime);
        }
        if (this.model) {
            const [nativeFrom, nativeTo] = this.model;
            if (!nativeTo) {
                const from = DateTime.fromISO(nativeFrom).startOf("day");
                if (this.minInterval) {
                    const minInterval = this.minInterval;
                    const minDuration = Duration.fromObject({ [minInterval.unit]: minInterval.value });
                    predicates.push(d => d.diff(from, minInterval.unit) < minDuration);
                }
                if (this.maxInterval) {
                    const maxInterval = this.maxInterval;
                    const maxDuration = Duration.fromObject({ [maxInterval.unit]: maxInterval.value });
                    predicates.push(d => d.diff(from, maxInterval.unit) > maxDuration);
                }
            }
        }
        for (const predicate of predicates) {
            const result = predicate(dayDateTime);
            if (result)
                return true;
        }
        return false;
    }
    onDaySelected() {
        if (!this.model)
            return;
        const [nativeFrom, nativeTo] = this.model;
        if (nativeFrom && nativeTo) {
            this.$emit("range-selected");
        }
    }
};
__decorate([
    ModelSync("value", "change", { required: false, default: () => null }),
    __metadata("design:type", Object)
], LvDateRangePicker.prototype, "model", void 0);
__decorate([
    Prop({ type: String, required: false, default: () => null }),
    __metadata("design:type", String)
], LvDateRangePicker.prototype, "minDate", void 0);
__decorate([
    Prop({ type: String, required: false, default: () => null }),
    __metadata("design:type", String)
], LvDateRangePicker.prototype, "maxDate", void 0);
__decorate([
    Prop({ type: String, required: false, default: null }),
    __metadata("design:type", String)
], LvDateRangePicker.prototype, "labelFrom", void 0);
__decorate([
    Prop({ type: String, required: false, default: null }),
    __metadata("design:type", String)
], LvDateRangePicker.prototype, "labelTo", void 0);
__decorate([
    Prop({ type: Object, required: false, default: null }),
    __metadata("design:type", Object)
], LvDateRangePicker.prototype, "minInterval", void 0);
__decorate([
    Prop({ type: Object, required: false, default: null }),
    __metadata("design:type", Object)
], LvDateRangePicker.prototype, "maxInterval", void 0);
__decorate([
    Ref(),
    __metadata("design:type", LvControlPopover)
], LvDateRangePicker.prototype, "popover", void 0);
__decorate([
    Ref(),
    __metadata("design:type", LvCalendarMonth)
], LvDateRangePicker.prototype, "leftCalendar", void 0);
__decorate([
    Ref(),
    __metadata("design:type", LvCalendarMonth)
], LvDateRangePicker.prototype, "rightCalendar", void 0);
__decorate([
    Watch("model"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], LvDateRangePicker.prototype, "onModelChange", null);
LvDateRangePicker = __decorate([
    Component({
        name: COMPONENT_NAME,
        responsive: true,
        components: {
            LvControlPopover,
            LvField,
            LvIcon,
            LvCalendarMonth,
        },
    })
], LvDateRangePicker);
export default LvDateRangePicker;
//# sourceMappingURL=script.js.map